import axios from 'axios';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Create axios instance with auth interceptor
const api = axios.create({
  baseURL: API_BASE,
  withCredentials: false
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Only handle 401 errors for actual auth failures, not network issues
    if (error.response?.status === 401) {
      const errorDetail = error.response?.data?.detail || '';
      // Skip interceptor for refresh requests to prevent infinite loops
      if (error.config?.url?.includes('/auth/refresh')) {
        return Promise.reject(error);
      }
      // Only logout for genuine auth failures (expired/invalid token)
      if (errorDetail.includes('Token expired') || 
          errorDetail.includes('Invalid token') || 
          errorDetail.includes('Not authenticated')) {
        // Try token refresh before logging out
        const token = localStorage.getItem('auth_token');
        if (token && !error.config._retried) {
          error.config._retried = true;
          try {
            const refreshRes = await api.post('/auth/refresh', {}, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (refreshRes.data?.token) {
              localStorage.setItem('auth_token', refreshRes.data.token);
              error.config.headers['Authorization'] = `Bearer ${refreshRes.data.token}`;
              return api(error.config);
            }
          } catch {
            // Refresh failed — proceed to logout
          }
        }
        localStorage.removeItem('auth_token');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Task APIs
export const taskApi = {
  getAll: (params) => api.get('/tasks', { params }),
  get: (id) => api.get(`/tasks/${id}`),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.patch(`/tasks/${id}`, data),
  delete: (id) => api.delete(`/tasks/${id}`),
  toggleComplete: (id) => api.post(`/tasks/${id}/complete`),
  updateStatus: (id, status) => api.patch(`/tasks/${id}/status`, { status }),
  addComment: (id, content, mentions = []) => api.post(`/tasks/${id}/comments`, { content, mentions })
};

// Focus Session APIs
export const focusApi = {
  start: (data) => api.post('/focus/start', data),
  complete: (id) => api.post(`/focus/${id}/complete`),
  getStats: () => api.get('/focus/stats')
};

// AI APIs
export const aiApi = {
  parseTask: (text) => api.post('/ai/parse-task', { text }),
  getSuggestions: (prompt, context) => api.post('/ai/suggest', { prompt, context }),
  planDay: () => api.post('/ai/plan-day'),
  getTip: () => api.get('/ai/tip')
};

// Dashboard APIs
export const dashboardApi = {
  getStats: () => api.get('/dashboard/stats')
};

// Workspace APIs
export const workspaceApi = {
  getAll: () => api.get('/workspaces'),
  get: (id) => api.get(`/workspaces/${id}`),
  create: (data) => api.post('/workspaces', data),
  update: (id, data) => api.patch(`/workspaces/${id}`, data),
  invite: (id, email, role) => api.post(`/workspaces/${id}/invite`, { email, role }),
  removeMember: (workspaceId, userId) => api.delete(`/workspaces/${workspaceId}/members/${userId}`),
  leave: (id) => api.post(`/workspaces/${id}/leave`),
  getProjects: (id) => api.get(`/workspaces/${id}/projects`),
  getTasks: (id, params) => api.get(`/workspaces/${id}/tasks`, { params })
};

// Project APIs
export const projectApi = {
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.patch(`/projects/${id}`, data)
};

// Chat APIs
export const chatApi = {
  getConversations: (workspaceId) => api.get('/conversations', { params: { workspace_id: workspaceId } }),
  createConversation: (data) => api.post('/conversations', data),
  getMessages: (conversationId, limit = 50, before) => 
    api.get(`/conversations/${conversationId}/messages`, { params: { limit, before } }),
  sendMessage: (data) => api.post('/messages', data),
  reactToMessage: (messageId, emoji) => api.post(`/messages/${messageId}/react`, { message_id: messageId, emoji }),
  createTaskFromMessage: (messageId) => api.post(`/messages/${messageId}/create-task`)
};

// Voice APIs
export const voiceApi = {
  transcribe: (formData) => api.post('/voice/transcribe', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  createTask: (transcription, language) => api.post('/voice/create-task', { transcription, language })
};

// Language APIs
export const languageApi = {
  getTranslations: (lang) => api.get(`/translations/${lang}`),
  getLanguages: () => api.get('/languages'),
  updateUserLanguage: (language) => api.patch('/user/language', { language })
};

// Admin APIs
export const adminApi = {
  // Users
  getUsers: (params) => api.get('/admin/users', { params }),
  getUser: (userId) => api.get(`/admin/users/${userId}`),
  updateUser: (userId, data) => api.patch(`/admin/users/${userId}`, data),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  
  // User Approval
  getPendingUsers: () => api.get('/admin/users/pending'),
  getApprovedUsers: (params) => api.get('/admin/users/approved', { params }),
  getRejectedUsers: () => api.get('/admin/users/rejected'),
  approveUser: (userId) => api.post(`/admin/users/${userId}/approval`, { action: 'approve' }),
  rejectUser: (userId, reason) => api.post(`/admin/users/${userId}/approval`, { action: 'reject', rejection_reason: reason }),
  
  // Active Users Monitoring
  getActiveUsers: () => api.get('/admin/users/active'),
  
  // Stats & Reports
  getStats: () => api.get('/admin/stats'),
  getUserReports: (period) => api.get('/admin/reports/users', { params: { period } }),
  getTaskReports: () => api.get('/admin/reports/tasks'),
  
  // Features
  getFeatures: () => api.get('/admin/features'),
  createFeature: (data) => api.post('/admin/features', null, { params: data }),
  updateFeature: (featureId, data) => api.patch(`/admin/features/${featureId}`, null, { params: data }),
  deleteFeature: (featureId) => api.delete(`/admin/features/${featureId}`),
  
  // Settings
  getSettings: () => api.get('/admin/settings'),
  updateSetting: (key, value) => api.post('/admin/settings', null, { params: { key, value } })
};

// Launchpad APIs
export const launchpadApi = {
  // Folders
  getFolders: () => api.get('/launchpad/folders'),
  getFolder: (folderId) => api.get(`/launchpad/folders/${folderId}`),
  createFolder: (data) => api.post('/launchpad/folders', data),
  updateFolder: (folderId, data) => api.patch(`/launchpad/folders/${folderId}`, data),
  deleteFolder: (folderId) => api.delete(`/launchpad/folders/${folderId}`),
  
  // Sharing
  shareFolder: (folderId, data) => api.post(`/launchpad/folders/${folderId}/share`, data),
  unshareFolder: (folderId, data) => api.delete(`/launchpad/folders/${folderId}/share`, { data }),
  getFolderShares: (folderId) => api.get(`/launchpad/folders/${folderId}/shares`),
  
  // Links
  createLink: (folderId, data) => api.post(`/launchpad/folders/${folderId}/links`, data),
  updateLink: (linkId, data) => api.patch(`/launchpad/links/${linkId}`, data),
  deleteLink: (linkId) => api.delete(`/launchpad/links/${linkId}`)
};

// Feedback APIs
export const feedbackApi = {
  getReceived: () => api.get('/my-feedback/received'),
  getGiven: () => api.get('/my-feedback/given'),
  getTeam: () => api.get('/my-feedback/team'),
  markAsRead: (submissionId) => api.post(`/my-feedback/received/${submissionId}/read`),
  markAllAsRead: () => api.post('/my-feedback/received/mark-all-read')
};

export default api;
